import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ACTION_BY_ID,
    AREA_COUNT,
    CONTROL_ACTIONS,
    DEFAULT_PINNED_IDS,
    DEFAULT_RECENT_IDS,
    RECENT_LIMIT,
    TOTAL_ACTION_COUNT,
    groupByArea,
    type AreaSection,
    type ControlAction,
} from './controlCenterMeta';
import {
    actionRefusal,
    destinationFor,
    surfaceFor,
    type ActionOutcome,
    type ActionRefusal,
    type ActionSurface,
} from './controlCenterSurfaces';
import { useCompactCatalog } from './useCompactCatalog';

/** Which half of the catalog the segmented filter is showing. */
/**
 * A collection key, spelled out rather than imported from
 * `@react-types/shared`: it is `string | number` there, `Set` is invariant in
 * TypeScript, and a local alias keeps the hook's type assignable to the
 * `Accordion` prop without pulling a transitive package into this folder.
 */
export type AreaKey = string | number;

/**
 * The refusal seam every entry point shares — just the resolver.
 *
 * Each refused row owns its **own** popover open state rather than the page
 * owning "which refusal is open", because one action is rendered in up to
 * three places at once — a pinned tile, a catalog row and a Recent chip — and a
 * single page-level answer would open all three at the same time.
 *
 * The press needs no handler here either. A refused card is not a button;
 * `Popover.Trigger` is, and it opens itself.
 */
export type ActionRefusals = {
    /** Why an action is refused, or `null` when it is not. */
    refusalFor: (actionId: string) => ActionRefusal | null;
};

export type UseControlCenterPageResult = {
    /** Every action, grouped into its area sections. */
    sections: AreaSection[];
    availableCount: number;
    areaCount: number;
    totalCount: number;

    pinned: ControlAction[];
    pinnedIds: string[];
    recent: ControlAction[];
    isEditingPins: boolean;
    onToggleEditPins: () => void;
    onTogglePin: (id: string) => void;
    isPinned: (id: string) => boolean;
    /** Drop `sourceId` onto `targetId`'s cell. A no-op for unknown ids. */
    onReorderPin: (sourceId: string, targetId: string) => void;
    /** The keyboard equivalent: one cell up (`-1`) or down (`1`). */
    onMovePin: (id: string, delta: number) => void;

    /**
     * The compact catalog's open areas, in the shape HeroUI's `Accordion`
     * wants: its `expandedKeys` and `onExpandedChange`. A `Set` of area slugs.
     */
    isCompact: boolean;
    expandedAreas: Set<AreaKey>;
    onExpandedAreasChange: (keys: Set<AreaKey>) => void;

    isPaletteOpen: boolean;
    onOpenPalette: () => void;
    onClosePalette: () => void;

    /** The action whose overlay is open, or `null`. */
    openAction: ControlAction | null;
    /**
     * The registered surface for `openAction`, when it has one — the feature's
     * own UI. `null` means the placeholder shell, which is still the honest
     * answer for an action nobody has wired yet.
     */
    openSurface: ActionSurface | null;
    /**
     * Bumped on every open, so the mounted surface gets a fresh `key`.
     *
     * The employee drawer seeds every field from its props once, at mount; a
     * surface reopened without a remount would show the previous draft. The
     * register learned this first and its rule travels with the component.
     */
    surfaceInstanceId: number;
    onRunAction: (action: ControlAction) => void;
    onCloseAction: () => void;

    /**
     * Everything a row needs to refuse in place, bundled — four entry points
     * render actions (pinned tile, catalog row, Recent chip, palette) and
     * threading three props through each of them is three chances to wire one
     * wrong.
     */
    refusals: ActionRefusals;

    /** The last completed action's confirmation, or `null`. */
    outcome: ActionOutcome | null;
    onDismissOutcome: () => void;
    onActionDone: (outcome: ActionOutcome) => void;
};

/** Was the Ctrl/Cmd + K chord pressed, on either platform? */
const isPaletteChord = (event: KeyboardEvent): boolean =>
    event.key.toLowerCase() === 'k' && (event.ctrlKey || event.metaKey);

/**
 * The Control Center's state.
 *
 * **It fetches nothing.** The catalog is a fixture in `controlCenterMeta`
 * (there is no endpoint behind it yet), so there is no query, no loading state
 * and no `ErrorBoundary` on this page — a skeleton would be standing in for
 * data that is never in flight, and a boundary would be guarding a subtree with
 * nothing to throw. What this hook owns is what the reader does: what is
 * pinned, what was run recently, which half of the catalog is showing, and
 * which of the three overlays is open.
 *
 * Pins and Recent live in component state rather than storage, deliberately:
 * where they are persisted is a decision for the API that will own them, and a
 * `localStorage` key written now is a migration to undo later.
 */
export const useControlCenterPage = (): UseControlCenterPageResult => {
    const [pinnedIds, setPinnedIds] = React.useState(DEFAULT_PINNED_IDS);
    const [recentIds, setRecentIds] = React.useState(DEFAULT_RECENT_IDS);
    const [isEditingPins, setIsEditingPins] = React.useState(false);
    const [expandedAreas, setExpandedAreas] = React.useState<Set<AreaKey>>(
        () => new Set(),
    );
    const [isPaletteOpen, setIsPaletteOpen] = React.useState(false);
    const [openActionId, setOpenActionId] = React.useState<string | null>(null);
    const [outcome, setOutcome] = React.useState<ActionOutcome | null>(null);
    const [surfaceInstanceId, setSurfaceInstanceId] = React.useState(0);
    const surfaceInstanceRef = React.useRef(0);

    const isCompact = useCompactCatalog();
    const navigate = useNavigate();

    const sections = React.useMemo(() => groupByArea(CONTROL_ACTIONS), []);

    // Resolved from ids rather than stored as objects so that the pinned grid,
    // the catalog's pin state and the palette's pin shortcut are all reading
    // one list. Unknown ids are dropped instead of rendering a blank tile.
    const pinned = React.useMemo(
        () =>
            pinnedIds.flatMap((id) => {
                const action = ACTION_BY_ID[id];
                return action ? [action] : [];
            }),
        [pinnedIds],
    );

    const recent = React.useMemo(
        () =>
            recentIds.flatMap((id) => {
                const action = ACTION_BY_ID[id];
                return action ? [action] : [];
            }),
        [recentIds],
    );

    const isPinned = React.useCallback(
        (id: string) => pinnedIds.includes(id),
        [pinnedIds],
    );

    const onTogglePin = React.useCallback((id: string) => {
        setPinnedIds((current) =>
            current.includes(id)
                ? current.filter((pinnedId) => pinnedId !== id)
                : // Appended, not prepended: a new pin lands in the next free
                  // cell of the grid, so pinning never reshuffles the five
                  // tiles someone has already learned the position of.
                  [...current, id],
        );
    }, []);

    const onToggleEditPins = React.useCallback(
        () => setIsEditingPins((current) => !current),
        [],
    );

    // Reordering is a splice on the id list, so the drag and the keyboard
    // arrows below are the same operation reached two ways rather than two
    // implementations of it.
    const movePinToIndex = React.useCallback((id: string, next: number) => {
        setPinnedIds((current) => {
            const from = current.indexOf(id);
            if (from === -1 || next < 0 || next >= current.length) {
                return current;
            }
            const reordered = [...current];
            reordered.splice(from, 1);
            reordered.splice(next, 0, id);
            return reordered;
        });
    }, []);

    const onReorderPin = React.useCallback(
        (sourceId: string, targetId: string) => {
            if (sourceId === targetId) {
                return;
            }
            setPinnedIds((current) => {
                const from = current.indexOf(sourceId);
                const to = current.indexOf(targetId);
                if (from === -1 || to === -1) {
                    return current;
                }
                const reordered = [...current];
                reordered.splice(from, 1);
                reordered.splice(to, 0, sourceId);
                return reordered;
            });
        },
        [],
    );

    const onMovePin = React.useCallback(
        (id: string, delta: number) =>
            movePinToIndex(id, pinnedIds.indexOf(id) + delta),
        [movePinToIndex, pinnedIds],
    );

    const onOpenPalette = React.useCallback(() => setIsPaletteOpen(true), []);
    const onClosePalette = React.useCallback(() => setIsPaletteOpen(false), []);

    const onRunAction = React.useCallback(
        (action: ControlAction) => {
            // The last line of defence, not the mechanism. A refused row renders as
            // a popover trigger and never calls this — but every other entry point
            // funnels through here, and a press that reached a surface whose every
            // write would 403 is worse than a press that does nothing.
            if (actionRefusal(action.id)) {
                return;
            }

            // Recent is written on the way in, not on a successful save: it is a
            // record of what someone reached for, and an action abandoned in its
            // modal is still an action they went looking for.
            setRecentIds((current) =>
                [action.id, ...current.filter((id) => id !== action.id)].slice(
                    0,
                    RECENT_LIMIT,
                ),
            );
            setIsPaletteOpen(false);
            // A new action supersedes the last confirmation: two banners would
            // leave the reader deciding which one is about what they just did.
            setOutcome(null);

            // The shared launcher survives route changes, so a destination
            // action must explicitly close any previously opened surface.
            const destination = destinationFor(action.id);
            if (destination) {
                setOpenActionId(null);
                navigate(destination.to);
                return;
            }

            surfaceInstanceRef.current += 1;
            setSurfaceInstanceId(surfaceInstanceRef.current);
            setOpenActionId(action.id);
        },
        [navigate],
    );

    const onCloseAction = React.useCallback(() => setOpenActionId(null), []);

    const refusals = React.useMemo<ActionRefusals>(
        () => ({ refusalFor: actionRefusal }),
        [],
    );

    const onActionDone = React.useCallback((next: ActionOutcome) => {
        // The surface reports the success; closing is this page's job, so a
        // surface never has to call two callbacks to finish.
        setOutcome(next);
        setOpenActionId(null);
    }, []);

    const onDismissOutcome = React.useCallback(() => setOutcome(null), []);

    // Ctrl/Cmd + K, bound on the window because it is a page-wide accelerator
    // rather than a key the search field handles: the point of the chord is
    // that focus does not have to be in the field already.
    React.useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (!isPaletteChord(event)) {
                return;
            }
            // The browser's own "search bookmarks" chord would otherwise fire
            // alongside it on Firefox.
            event.preventDefault();
            setIsPaletteOpen(true);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    return {
        sections,
        availableCount: CONTROL_ACTIONS.length,
        areaCount: AREA_COUNT,
        totalCount: TOTAL_ACTION_COUNT,

        pinned,
        pinnedIds,
        recent,
        isEditingPins,
        onToggleEditPins,
        onTogglePin,
        isPinned,
        onReorderPin,
        onMovePin,

        isCompact,
        expandedAreas,
        onExpandedAreasChange: setExpandedAreas,

        isPaletteOpen,
        onOpenPalette,
        onClosePalette,

        openAction: openActionId ? (ACTION_BY_ID[openActionId] ?? null) : null,
        openSurface: openActionId ? surfaceFor(openActionId) : null,
        surfaceInstanceId,
        onRunAction,
        onCloseAction,

        refusals,

        outcome,
        onDismissOutcome,
        onActionDone,
    };
};
